import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Dashboard.css';
import api from '../../utils/axiosConfig';
// import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { CardContent, Typography, Grid, Button, IconButton, Tooltip, Box, Collapse } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import { styled, keyframes } from '@mui/system';
import { setUserType } from '../../store/authSlice.js';
import { useDispatch } from 'react-redux';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import Battery90Icon from '@mui/icons-material/Battery90';
import Battery80Icon from '@mui/icons-material/Battery80';
import Battery50Icon from '@mui/icons-material/Battery50';
import Battery30Icon from '@mui/icons-material/Battery30';
import Battery20Icon from '@mui/icons-material/Battery20';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import BatteryCharging90Icon from '@mui/icons-material/BatteryCharging90';
import BatteryCharging80Icon from '@mui/icons-material/BatteryCharging80';
import BatteryCharging50Icon from '@mui/icons-material/BatteryCharging50';
import BatteryCharging30Icon from '@mui/icons-material/BatteryCharging30';
import BatteryCharging20Icon from '@mui/icons-material/BatteryCharging20';
// import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
// import SignalCellularAlt2BarIcon from '@mui/icons-material/SignalCellularAlt2Bar';
// import SignalCellularAlt1BarIcon from '@mui/icons-material/SignalCellularAlt1Bar';
import StyledCard from '../StyledCard.js';
import MapComponent from '../Maps/Map.js';
import SignalStrength from '../SignalStrength.js';
import AdminNav from '../AdminNav/AdminNav.js';
import { toast } from 'react-toastify';

const inFlightUnitDataRequests = new Map();

const fetchUnitDataOnce = (unitSerNum, originalData) => {
    if (!unitSerNum) {
        return Promise.resolve(null);
    }

    const requestKey = `${unitSerNum}::${JSON.stringify(originalData || {})}`;
    if (inFlightUnitDataRequests.has(requestKey)) {
        return inFlightUnitDataRequests.get(requestKey);
    }

    const requestPromise = api.post('/api/unitData', originalData || {}, {
        params: {
            unit: unitSerNum,
        }
    }).then((response) => response.data)
        .finally(() => {
            inFlightUnitDataRequests.delete(requestKey);
        });

    inFlightUnitDataRequests.set(requestKey, requestPromise);
    return requestPromise;
};

// TODOS: 
// Get advanced mode and step count
// Steps taken over x amount of time but only if parameter is enabled

// Non mvp
// call device -- Eventually

function isEmpty(...values) {
    return values.some(value =>
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && value !== null && Object.keys(value).length === 0)
    );
}

// Add ripple animation
const rippleEffect = keyframes`
    0% { 
        background-position: 0% 50%;
        background-size: 300% 100%;
    }
    100% { 
        background-position: 100% 50%;
        background-size: 300% 100%;
    }
`;


const StatusText = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'isUpdating',
})(({ isUpdating, theme }) => ({
    animation: isUpdating ? `${rippleEffect} 4s linear` : 'none',
    background: isUpdating ? `linear-gradient(
        90deg,
        #007bff 0%,
        #00ff00 50%,
        #007bff 100%
    )` : 'none',
    backgroundSize: '300% 100%',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: isUpdating ? 'transparent' : 'inherit',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    display: 'inline-block',
    position: 'relative',
    width: '100%'
}));


const SubHeading = styled(Typography)({
    marginTop: (theme) => theme.spacing(1),
    marginBottom: (theme) => theme.spacing(1),
});

const Dashboard = () => {
    // const classes = useStyles();
    const activeUnit = useSelector(state => state.auth.activeUnit);
    const userType = useSelector(state => state.auth.userType ? state.auth.userType.userType : null);
    const dispatch = useDispatch();
    const [unitStatus, setUnitStatus] = useState(null);
    const [stepsTakenToday, setStepsTakenToday] = useState(null);
    const [lastKnownLocation, setLastKnownLocation] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
    const [shouldFetchData, setShouldFetchData] = useState(false);
    const [isFetchingLocation, setIsFetchingLocation] = useState(false);
    const [isChiming, setIsChiming] = useState(false);
    // Check if user has permission to use location and chime features
    const hasLocationPermission = userType === 'Interactive' || userType === 'Admin';
    const isAdmin = userType === 'Admin';
    // const [socket, setSocket] = useState(null);
    // const [socketId, setSocketId] = useState(null);
    const [noResults, setNoResults] = useState(false);
    const [initialLocation, setInitialLocation] = useState(null);
    const [lastPingTime, setLastPingTime] = useState(null);
    const [userInitiatedUpdate, setUserInitiatedUpdate] = useState(false);
    const [originalData, setOriginalData] = useState({});
    const [stopFetchingLocation, setStopFetchingLocation] = useState(false);
    const intervalRef = useRef(null);
    const previousLocationRef = useRef(null);
    const originalDataRef = useRef({});
    const [isUsingHomeLocation, setIsUsingHomeLocation] = useState(false);
    const [showSteps, setShowSteps] = useState(false);
    const [steps, setSteps] = useState([]);
    const [showPhoneNumbers, setShowPhoneNumbers] = useState(false);
    const [userPhoneNumber, setUserPhoneNumber] = useState(null);
    const [isCreatingContact, setIsCreatingContact] = useState(false);
    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        // Also want the timezone
        return date.toLocaleString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
            timeZoneName: 'short'
        });
    };

    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return 'Not available';
        
        // Remove all non-digits
        const digits = phoneNumber.replace(/\D/g, '');
        
        // Format as (XXX) XXX-XXXX
        if (digits.length === 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        } else if (digits.length === 11 && digits[0] === '1') {
            return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
        }
        
        // Return as-is if format doesn't match
        return phoneNumber;
    };

    const handleAddToContacts = async () => {
        const customerName = [activeUnit?.FirstName, activeUnit?.LastName]
            .filter(Boolean)
            .join(' ') || activeUnit?.CustomerAlias || 'Device User';

        setIsCreatingContact(true);
        try {
            const response = await api.post('/api/contacts/device-link', {
                customerName,
                callDevice: unitStatus?.unitPhoneNumber,
                incomingDeviceCalls: unitStatus?.receiveCallsFrom,
                textDevice: unitStatus?.smsPhoneNumber
            });

            window.location.assign(response.data.url);
        } catch (error) {
            console.error('Error creating device contact:', error);
            toast.error('Unable to create the contact card. Please try again.');
        } finally {
            setIsCreatingContact(false);
        }
    };

    const [isStatusUpdating, setIsStatusUpdating] = useState(false);
    
    // Check if unit is a Smart Connect unit
    const isSmartConnectUnit = activeUnit?.MC && /smart connect/i.test(activeUnit.MC);
    
    const getBatteryIcon = (ACStatus, batteryPercent) => {
        const getColor = () => {
            if (batteryPercent > 50) {
                return 'green';
            } else if (batteryPercent > 20) {
                return '#FF8C00';
            } else {
                return 'red';
            }
        };
        const color = getColor();
        const sx = { color: color, transform: 'rotate(90deg)', verticalAlign: 'middle' };
        if (ACStatus === 1) {
            if (batteryPercent > 90) {
                return <BatteryChargingFullIcon sx={sx} />;
            } else if (batteryPercent > 80) {
                return <BatteryCharging90Icon sx={sx} />;
            } else if (batteryPercent > 60) {
                return <BatteryCharging80Icon sx={sx} />;
            } else if (batteryPercent > 40) {
                return <BatteryCharging50Icon sx={sx} />;
            } else if (batteryPercent > 20) {
                return <BatteryCharging30Icon sx={sx} />;
            } else if (batteryPercent > 0) {
                return <BatteryCharging20Icon sx={sx} />;
            } else {
                return <BatteryAlertIcon sx={sx} />;
            }
        } else if (batteryPercent > 90) {
            return <BatteryFullIcon sx={sx} />;
        } else if (batteryPercent > 80) {
            return <Battery90Icon sx={sx} />;
        } else if (batteryPercent > 60) {
            return <Battery80Icon sx={sx} />;
        } else if (batteryPercent > 40) {
            return <Battery50Icon sx={sx} />;
        } else if (batteryPercent > 20) {
            return <Battery30Icon sx={sx} />;
        } else if (batteryPercent > 0) {
            return <Battery20Icon sx={sx} />;
        } else {
            return <BatteryAlertIcon sx={sx} />;
        }
    };


    const updateLocation = async (LocateType) => {
        setInitialLocation(lastKnownLocation);
        previousLocationRef.current = JSON.stringify(lastKnownLocation);
        if (LocateType === 'WiFi') {
            setUserInitiatedUpdate(true);
            if (isFetchingLocation) return;
            setIsFetchingLocation(true);
        } else {
            if (isChiming) return;
            setIsChiming(true);
            setTimeout(() => setIsChiming(false), 30000);
        }
        try {
            setInitialLocation(lastKnownLocation);
            const body = { UnitSerNum: unitStatus.UnitSerNum, LocateType: LocateType };
            await api.post('/api/unit/reportLocation', body);
            let counter = 0;
            if (LocateType === 'WiFi') {
                toast.success('Location should update within 30 seconds.')
            } else {
                toast.success('Chime should start within 30 seconds.')
            }

            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            const intervalId = setInterval(() => {
                setShouldFetchData(true);
                counter++;

                // Stop after 6 attempts (60 seconds)
                if (counter >= 6) {
                    clearInterval(intervalId);
                    // Reset flags if we never got an update
                    setUserInitiatedUpdate(false);
                    setIsFetchingLocation(false);
                    setIsChiming(false);
                }
            }, 10000); // 10000 milliseconds = 10 seconds
            intervalRef.current = intervalId;
            // setUserInitiatedUpdate(false);
            // setShouldFetchData(true);
        } catch (error) {
            setUserInitiatedUpdate(false);
            setIsFetchingLocation(false);
            setIsChiming(false);
        }
    };
    useEffect(() => {
        if (!lastKnownLocation || !previousLocationRef.current) return;

        const prevLocation = JSON.parse(previousLocationRef.current);

        if (prevLocation &&
            (lastKnownLocation.Latitude !== prevLocation.Latitude ||
                lastKnownLocation.Longitude !== prevLocation.Longitude)) {

            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }

            setShouldFetchData(false);


            if (userInitiatedUpdate) {
                setIsStatusUpdating(true);
                setTimeout(() => setIsStatusUpdating(false), 4000);

                setUserInitiatedUpdate(false);
                setIsFetchingLocation(false);
                setIsChiming(false);
            }
        }
    }, [lastKnownLocation, userInitiatedUpdate]);


    // Update ref whenever originalData changes
    useEffect(() => {
        originalDataRef.current = originalData;
    }, [originalData]);

    const fetchData = useCallback(async () => {
        if (!activeUnit) return;

        setIsLoading(true);
        try {
            // Use ref to get current originalData without depending on it
            const currentOriginalData = originalDataRef.current;
            const data = await fetchUnitDataOnce(activeUnit.UnitSerNum, currentOriginalData);
            dispatch(setUserType({ userType: data.userMap }));
            setIsUsingHomeLocation(data.useHomeLocation);
            setOriginalData(data);
            setHasInitiallyLoaded(true);
            
            // Set user's phone number and unit data from numbersToBeAwareOf
            if (data.numbersToBeAwareOf) {
                setUserPhoneNumber(data.numbersToBeAwareOf.userPhoneNumber);
                // Store the complete numbers object in unitStatus for later use
                if (data.unitStatus) {
                    data.unitStatus.receiveCallsFrom = data.numbersToBeAwareOf.receiveCallsFrom;
                    data.unitStatus.unitPhoneNumber = data.numbersToBeAwareOf.unitPhoneNumber;
                    data.unitStatus.userPhoneNumber = data.numbersToBeAwareOf.userPhoneNumber;
                    data.unitStatus.smsPhoneNumber = data.numbersToBeAwareOf.smsPhoneNumber;
                }
            }

            if (isEmpty(data.unitStatus)) {
                setNoResults(true);
                setIsLoading(false);
            } else {
                if (data.unitStatus.noLocationDataFound) {
                    setNoResults(true);
                }

                // Use functional updates to avoid dependency on current state values
                setLastPingTime(prevLastPingTime => {
                    if (data.unitStatus.lastPingTime !== prevLastPingTime) {
                        // Check if this was a user-initiated update
                        setUserInitiatedUpdate(prevUserInitiated => {
                            if (prevUserInitiated) {
                                setIsStatusUpdating(true);
                                setTimeout(() => setIsStatusUpdating(false), 4000);

                                // Reset the status flags
                                setIsFetchingLocation(false);
                                setIsChiming(false);
                                return false; // Set userInitiatedUpdate to false
                            }
                            return prevUserInitiated;
                        });
                    }
                    return data.unitStatus.lastPingTime;
                });

                setUnitStatus(data.unitStatus);
                setLastKnownLocation(data.lastKnownLocation);
                setNoResults(false);
                setIsLoading(false);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setHasInitiallyLoaded(true);
            setIsLoading(false);
        }
    }, [activeUnit, dispatch]); // Only depend on stable values

    useEffect(() => {
        if (shouldFetchData && activeUnit) {
            setShouldFetchData(false);
        } else if (activeUnit) {
            fetchData();
        }
    }, [activeUnit, shouldFetchData, fetchData]);

    // useEffect(() => {
    //     if (!activeUnit || !showSteps) return;
    //     async function fetchSteps() {
    //         try {
    //             const stepsResponse = await api.get('/api/unit/stepsSummary', {
    //                 params: {
    //                     unit: activeUnit.UnitSerNum,
    //                 }
    //             });
    //             if (stepsResponse.data && stepsResponse.data.StepsCount !== undefined) {
    //                 console.log('Steps taken today:', stepsResponse.data.StepsCount);
    //                 setStepsTakenToday(stepsResponse.data.StepsCount || 0);
    //             }
    //         } catch (error) {
    //             console.error('Error fetching steps summary:', error);
    //         }
    //     }
    //     fetchSteps();

    // }, [showSteps]);

    useEffect(() => {

        if (
            initialLocation && lastKnownLocation &&
            (lastKnownLocation.Latitude !== initialLocation.Latitude ||
                lastKnownLocation.Longitude !== initialLocation.Longitude)
        ) {
            clearInterval(intervalRef.current);
        } else {
            if (stopFetchingLocation === true) {
                clearInterval(intervalRef.current);
                setStopFetchingLocation(false);
            }
        }
    }, [lastKnownLocation, initialLocation, stopFetchingLocation]);

    return (
        <>
            {/* Admin Navigation Bar - only visible for admin users */}
            {isAdmin && activeUnit && (
                <AdminNav customerKey={activeUnit.CustomerKey} />
            )}

            <Box sx={{ maxWidth: '800px', margin: '0 auto' }}>
                {!hasInitiallyLoaded ? (
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <Typography variant="h6">Loading dashboard...</Typography>
                    </Box>
                ) : hasInitiallyLoaded && !isLoading && (noResults || !unitStatus) ? (
                    <Box>
                        <Typography variant="h6" color="error">Could not get unit status.</Typography>
                    </Box>
                ) : (
                    <>
                        {(unitStatus && !!lastKnownLocation) && (
                            <>
                                {lastKnownLocation && lastKnownLocation.Latitude && lastKnownLocation.Longitude && (
                                    <Box sx={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                                        <MapComponent lat={lastKnownLocation.Latitude} lng={lastKnownLocation.Longitude} address={lastKnownLocation.address} eventTime={formatDateTime(lastKnownLocation.EventTime)} isUsingHomeLocation={isUsingHomeLocation} />
                                    </Box>
                                )}

                                <StyledCard sx={{ height: '100%', width: '100%' }}>
                                    <CardContent>
                                        <Grid container alignItems="center">
                                            <Grid item>
                                                <StatusText
                                                    variant="h6"
                                                    sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}
                                                    isUpdating={isStatusUpdating}
                                                >
                                                    Status as of: {formatDateTime(unitStatus.lastPingTime)}
                                                </StatusText>
                                            </Grid>
                                        </Grid>
                                        <SubHeading>
                                            <strong>Serial Number:</strong> {unitStatus.UnitSerNum}
                                        </SubHeading>
                                        {/* <SubHeading>
                                            <strong>Nearest Address:</strong> {lastKnownLocation.address || 'Could not fetch nearest address'}
                                        </SubHeading> */}
                                        <SubHeading>
                                            <strong>Powered On:</strong> {(unitStatus.switchPosition === 1) ? 'Yes' : 'No'}
                                        </SubHeading>
                                        <SubHeading>
                                            <strong>Charging:</strong> {(unitStatus.acPower === 1) ? 'Yes' : 'No'}
                                        </SubHeading>

                                        <SubHeading>
                                            <strong>Battery:</strong> {getBatteryIcon(unitStatus.acPower, unitStatus.charged)} {unitStatus.charged}%
                                        </SubHeading>

                                        <SubHeading>
                                            <strong>Signal Strength:</strong> <SignalStrength bars={unitStatus.bars} />
                                        </SubHeading>
                                        {showSteps === true && (
                                            <SubHeading>
                                                <strong>Steps Taken Today:</strong> {stepsTakenToday || 0}
                                            </SubHeading>
                                        )}

                                        {unitStatus.NextPing && (
                                            <Grid container alignItems="center" wrap="nowrap" sx={{ mb: 1 }}>
                                                <Grid item xs>
                                                    <SubHeading sx={{ mb: 0 }}>
                                                        <strong>Next Scheduled Check-In:</strong> {formatDateTime(unitStatus.NextPing)}
                                                    </SubHeading>
                                                </Grid>
                                                <Grid item>
                                                    <Tooltip
                                                        title={
                                                            <Typography sx={{ fontSize: '1.1rem', padding: '10px' }}>
                                                                This device is scheduled to automatically checkin once every 24 hours.
                                                            </Typography>
                                                        }
                                                        arrow
                                                        enterTouchDelay={0}
                                                        leaveTouchDelay={3000}
                                                        sx={{
                                                            tooltip: {
                                                                fontSize: '1.1rem',
                                                                backgroundColor: '#007bff',
                                                                padding: '10px',
                                                            }
                                                        }}
                                                    >
                                                        <IconButton sx={{ ml: 0.5, p: 0.5 }}>
                                                            <InfoIcon color="primary" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                            </Grid>
                                        )}

                                        {isSmartConnectUnit && (
                                            <>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            opacity: 0.7
                                                        }
                                                    }}
                                                    onClick={() => setShowPhoneNumbers(!showPhoneNumbers)}
                                                >
                                                    <Typography sx={{ fontWeight: 'bold' }}>
                                                        Numbers to be aware of and save to your contacts
                                                    </Typography>
                                                    <IconButton size="small" sx={{ ml: 0.5 }}>
                                                        {showPhoneNumbers ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </IconButton>
                                                </Box>
                                                <Collapse in={showPhoneNumbers}>
                                                    <Box sx={{ pl: 2, pt: 1, pb: 1 }}>
                                                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                                                            <strong>The number you'll get calls from:</strong>
                                                            <br />
                                                                    {formatPhoneNumber(unitStatus.receiveCallsFrom)}
                                                            <br />
                                                        </Typography>

                                                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                                                            <strong>The number you need to call to reach the device:</strong>
                                                            <br />
                                                            {formatPhoneNumber(unitStatus.unitPhoneNumber)}
                                                            <br />
                                                        </Typography>


                                                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                                                            <strong>The number you need to call from:</strong>
                                                            <br />
                                                                {formatPhoneNumber(unitStatus.userPhoneNumber)}
                                                            <br />
                                                        </Typography>

                                                        <Typography variant="body2" component="div">
                                                            <strong>Text communication & location requests:</strong>
                                                            <ul>
                                                                <li>Text "Location" to <strong>{formatPhoneNumber(unitStatus.smsPhoneNumber)}</strong> to receive a real-time Google Maps link showing the device's current location.  </li>
                                                                <li>
                                                                    You will also receive text notifications at this number when the help button is pressed.
                                                                </li>
                                                            </ul>
                                                        </Typography>
                                                        <Button
                                                            onClick={handleAddToContacts}
                                                            disabled={isCreatingContact}
                                                            variant="outlined"
                                                            startIcon={<ContactPhoneIcon />}
                                                            sx={{ mt: 0.5 }}
                                                        >
                                                            {isCreatingContact ? 'Preparing Contact...' : 'Open Contact Card'}
                                                        </Button>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ display: 'block', mt: 1 }}
                                                        >
                                                            This opens a contact card but does not save it automatically.
                                                            <br />
                                                            <strong>iPhone:</strong> Tap Create New Contact, then Done.
                                                            <br />
                                                            <strong>Android:</strong> Open the downloaded .vcf file, choose an account if asked, then tap Import. If it does not open, use Contacts &gt; Organize &gt; Import from file.
                                                        </Typography>
                                                    </Box>
                                                </Collapse>
                                            </>
                                        )}

                                        {(activeUnit?.UnitModel === 'myConnect Air' || unitStatus.workingMode === 'advanced') && lastKnownLocation && lastKnownLocation.Latitude && lastKnownLocation.Longitude && hasLocationPermission && (
                                            <Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                                                <Grid item sm={6} xs={12}>
                                                    <Button
                                                        onClick={() => updateLocation('WiFi')}
                                                        disabled={isFetchingLocation}
                                                        fullWidth
                                                        variant="contained"
                                                        color="primary"
                                                        sx={{ mt: 1, mb: 1 }}
                                                    >
                                                        {isFetchingLocation ? 'Updating Location...' : 'Update Location(Silent)'}
                                                    </Button>
                                                </Grid>
                                                {(activeUnit && !['myWatch', 'myConnect Air'].includes(activeUnit.UnitModel)) &&
                                                    <Grid item sm={6} xs={12}>
                                                        <Button
                                                            onClick={() => updateLocation('Chime')}
                                                            disabled={isChiming}
                                                            fullWidth
                                                            variant="contained"
                                                            color="primary"
                                                            sx={{ mt: { xs: .5, sm: 1 }, mb: 1 }}
                                                        >
                                                            {isChiming ? 'Chiming...' : 'Chime To Locate'}
                                                        </Button>
                                                    </Grid>
                                                }

                                            </Grid>
                                        )}

                                    </CardContent>
                                </StyledCard>
                            </>
                        )}
                    </>
                )}

                {hasInitiallyLoaded && !isLoading && !lastKnownLocation && (
                    <Box>
                        <Typography variant="h6" color="error">Could not get last known location.</Typography>
                    </Box>
                )}
            </Box>
        </>
    );
};

export default Dashboard;
